/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw } from 'lucide-react';
import api from '../../services/api';
import {
  providerToLlmPreference,
  formatDropdownLabel,
  formatGeneratedBy
} from '../../utils/llmProviderLabels';

const RoadmapView = () => {
  const { user } = useAuth();
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedModel, setSelectedModel] = useState('auto');
  const [ollamaAvailable, setOllamaAvailable] = useState(false);
  // /api/llm/providers registry — source of truth for dropdown labels.
  // Null until fetched; if fetch fails, the dropdown falls back to the
  // 'auto' option only (safe minimum) so a provider-registry outage
  // never blocks the user from generating a roadmap.
  const [availableProviders, setAvailableProviders] = useState(null);

  // Check if Ollama (local models) is available
  useEffect(() => {
    const checkOllama = async () => {
      try {
        const response = await api.get('/health');
        // Check if we're on localhost or if Ollama is explicitly available
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const ollamaStatus = response.data?.services?.ollama === 'connected';
        setOllamaAvailable(isLocalhost || ollamaStatus);
      } catch {
        setOllamaAvailable(false);
      }
    };
    checkOllama();
  }, []);

  // Fetch live provider registry so dropdown labels track configured
  // model IDs, not hardcoded strings. Silent on failure — dropdown
  // still renders 'auto' + the SLM section.
  useEffect(() => {
    const loadProviders = async () => {
      try {
        const resp = await api.get('/llm/providers');
        if (resp?.success && Array.isArray(resp.providers)) {
          setAvailableProviders(resp.providers);
        }
      } catch {
        setAvailableProviders(null);
      }
    };
    loadProviders();
  }, []);

  useEffect(() => {
    loadRoadmap();
  }, []);

  const loadRoadmap = async () => {
    try {
      const response = await api.getRoadmap(user.id, user.vertical);
      if (response.success) {
        setRoadmap(response.data);
      }
    } catch (error) {
      console.error('Error loading roadmap:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRoadmap = async () => {
    setGenerating(true);
    try {
      let llmPreference = selectedModel;
      let modelName = 'llama3.1:8b';

      if (selectedModel.startsWith('slm:')) {
        llmPreference = 'slm';
        modelName = selectedModel.replace('slm:', '');
      }

      console.log('🔄 Generating roadmap with:', { llmPreference, modelName });

      const response = await api.generateRoadmap(
        user.id,
        user.vertical || 'WMS',
        llmPreference,
        modelName
      );

      console.log('✅ Generation response:', response);

      if (response.success) {
        await loadRoadmap();
        alert('✅ Roadmap regenerated successfully!\n\nCheck backend console for Neo4j sync logs.');
      } else {
        alert('❌ Generation failed: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Generation error:', error);
      alert('❌ Failed to generate roadmap: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading roadmap...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* HEADER WITH GENERATE BUTTON - ALWAYS VISIBLE */}
      <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-xl p-6 shadow-lg border-2 border-purple-200 mb-8">
        <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              My Learning Roadmap
            </h1>
            <p className="text-gray-600">
              {roadmap ? `${roadmap.experienceLevel} level • ${roadmap.vertical} Track` : 'Generate your personalized learning path'}
            </p>
          </div>
          
          {/* GENERATE BUTTON SECTION */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full lg:w-auto">
            <select 
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="px-4 py-2.5 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white font-medium shadow-sm hover:border-purple-400 transition-colors"
            >
              <option value="auto">🤖 Auto (Best Available)</option>
              {ollamaAvailable && (
                <optgroup label="Local Models (Free - Requires Ollama)">
                  <option value="slm:llama3.1:8b">🦙 LLaMA 3.1 (8B)</option>
                  <option value="slm:mistral:7b">⚡ Mistral (7B)</option>
                  <option value="slm:gemma2:9b">💎 Gemma 2 (9B)</option>
                  <option value="slm:qwen2.5:7b">🌟 Qwen 2.5 (7B)</option>
                </optgroup>
              )}
              {/* Cloud provider options — built from the live registry.
                  Labels reflect what's actually configured (env-driven
                  model IDs), not hardcoded strings that drift. When a
                  provider ships a new ID, the dropdown updates on the
                  next page load with no code change. */}
              {availableProviders && (
                <>
                  <optgroup label="Cloud Models (API Key Required)">
                    {availableProviders
                      .filter((p) => p.enabled && ['groq', 'gemini', 'anthropic', 'openai'].includes(p.name) && p.models.paid)
                      .map((p) => (
                        <option
                          key={`${p.name}-paid`}
                          value={providerToLlmPreference(p.name, 'paid')}
                        >
                          {formatDropdownLabel(p.name, p.models.paid, 'paid')}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Free Tier Models">
                    {availableProviders
                      .filter((p) => p.enabled && ['groq', 'gemini'].includes(p.name) && p.models.free)
                      .map((p) => (
                        <option
                          key={`${p.name}-free`}
                          value={providerToLlmPreference(p.name, 'free')}
                        >
                          {formatDropdownLabel(p.name, p.models.free, 'free')}
                        </option>
                      ))}
                  </optgroup>
                </>
              )}
            </select>

            <button
              onClick={handleGenerateRoadmap}
              disabled={generating}
              className="px-8 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-bold shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 whitespace-nowrap min-w-[200px]"
            >
              <RefreshCw className={`w-5 h-5 ${generating ? 'animate-spin' : ''}`} />
              <span>{generating ? 'Generating...' : roadmap ? 'Regenerate Roadmap' : 'Generate Roadmap'}</span>
            </button>
          </div>
        </div>

        {/* Helper text */}
        <div className="mt-4 text-sm text-gray-600 bg-white/50 rounded-lg p-3">
          {selectedModel === 'auto' && (ollamaAvailable
            ? '💡 Will try local model first, fallback to cloud if needed'
            : '💡 Will use best available cloud model'
          )}
          {selectedModel.startsWith('slm:') && '💡 Using local model (requires Ollama running on port 11434)'}
          {selectedModel === 'claude' && '💡 Using Claude AI (requires ANTHROPIC_API_KEY)'}
          {selectedModel === 'openai' && '💡 Using OpenAI GPT-4 (requires OPENAI_API_KEY)'}
          {selectedModel === 'gemini' && '💡 Using Google Gemini Pro (requires GEMINI_API_KEY)'}
          {selectedModel === 'groq' && '💡 Using Groq LLaMA 70B (requires GROQ_API_KEY)'}
          {selectedModel === 'groq-free' && '💡 Using Groq Free Tier - Fast inference, 8B model'}
          {selectedModel === 'gemini-free' && '💡 Using Gemini Flash - Free tier, great for roadmaps'}
          {!ollamaAvailable && !selectedModel.startsWith('slm:') && selectedModel !== 'auto' && selectedModel !== 'claude' && selectedModel !== 'openai' && selectedModel !== 'gemini' && selectedModel !== 'groq' && selectedModel !== 'groq-free' && selectedModel !== 'gemini-free' && '💡 Select a model to generate your roadmap'}
        </div>
      </div>

      {/* ROADMAP CONTENT */}
      {roadmap ? (
        <div className="space-y-6">
          {/* Roadmap Summary */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Roadmap Overview</h2>
            
            {roadmap.professionalSummary && (
              <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                <p className="text-gray-800 leading-relaxed">{roadmap.professionalSummary}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Experience Level</p>
                <p className="text-2xl font-bold text-purple-600">{roadmap.experienceLevel}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Years of Experience</p>
                <p className="text-2xl font-bold text-blue-600">{roadmap.experienceYears}+ years</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Skills</p>
                <p className="text-2xl font-bold text-green-600">
                  {roadmap.learningPath?.reduce((sum, level) => sum + (level.skills?.length || 0), 0) || 0}
                </p>
              </div>
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Generated by:</strong> {formatGeneratedBy(roadmap.generatedBy)}</p>
              <p><strong>Confidence:</strong> {Math.round((roadmap.confidence || 0) * 100)}%</p>
              <p><strong>Last Updated:</strong> {new Date(roadmap.updatedAt).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Learning Path */}
          {roadmap.learningPath && roadmap.learningPath.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Learning Path</h2>
              
              <div className="space-y-6">
                {roadmap.learningPath.map((level, idx) => (
                  <div key={idx} className="relative">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
                        idx === 0 ? 'bg-green-500' : idx === 1 ? 'bg-blue-500' : 'bg-purple-500'
                      }`}>
                        {idx + 1}
                      </div>
                      
                      <div className="flex-1">
                        <div className="bg-gray-50 rounded-lg p-5">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-xl font-bold text-gray-800">{level.level}</h3>
                            <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full">
                              {level.duration}
                            </span>
                          </div>
                          
                          {level.skills && level.skills.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {level.skills.map((skill, skillIdx) => {
                                const skillName = typeof skill === 'string' ? skill : skill.name;
                                return (
                                  <div key={skillIdx} className="flex items-start gap-2 text-gray-700 bg-white p-3 rounded-lg shadow-sm">
                                    <span className="text-green-500 mt-0.5">✓</span>
                                    <span className="text-sm">{skillName}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">No skills defined for this level</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {idx < roadmap.learningPath.length - 1 && (
                      <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-300"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Skills Gap */}
          {roadmap.skillsGap && roadmap.skillsGap.length > 0 && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-orange-800 mb-3">Skills Gap Analysis</h3>
              <div className="flex flex-wrap gap-2">
                {roadmap.skillsGap.map((gap, idx) => (
                  <span key={idx} className="px-3 py-1 bg-orange-200 text-orange-800 rounded-full text-sm font-medium">
                    {gap}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        // NO ROADMAP STATE
        <div className="bg-white rounded-xl shadow-lg p-12 border-2 border-dashed border-gray-300 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Roadmap Yet</h2>
            <p className="text-gray-600 mb-6">
              Click the "Generate Roadmap" button above to create your personalized learning path using AI.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <p className="text-sm text-blue-800 font-medium mb-2">✨ What you'll get:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Personalized skills based on your resume</li>
                <li>• Learning path matched to your experience level</li>
                <li>• Skills organized by difficulty (Beginner → Advanced)</li>
                <li>• Automatically synced to your Knowledge Graph</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoadmapView;